import { ApiEndpoint } from './ApiDefinition';
import { JSON, JSONArray, JSONDateTime, JSONObject } from './types';


export const AuthApi = {
  getADClientId: ApiEndpoint<void, string>({
    url: 'security/ad-client-id'
  }),

  login: ApiEndpoint<{
    email: string,
    password: string,
    /**
     * The tenant entry point this sign-in came through (`/t/<slug>`), when it
     * came through one. Omitted on the plain entry point, which accepts any
     * tenant's user.
     *
     * It can only NARROW the sign-in: the server refuses a user who does not
     * belong to the named tenant, and it has no power to move anyone into one.
     */
    tenantSlug?: string | null
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/login',
    body: ({ email, password, tenantSlug }) => ({
      email: email || "",
      password: password || "",
      ...(tenantSlug ? { tenantSlug } : {})
    })
  }),

  /**
   * The workspaces a set of credentials can sign in to.
   *
   * An email identifies one person WITHIN a tenant, so the plain entry point
   * cannot serve an address that two tenants share: the server refuses it
   * rather than picking a row, and the refusal is worded like a wrong password
   * so it gives nothing away to an outsider. That leaves the person whose
   * address it is with no way to learn which `/t/<slug>` door is theirs — this
   * is how the sign-in page asks on their behalf.
   *
   * Safe to call unauthenticated because the server verifies the password
   * BEFORE naming anything: wrong credentials answer with an empty list,
   * exactly like an address that does not exist.
   */
  loginTenants: ApiEndpoint<{
    email: string,
    password: string
  }, TenantEntryPointDTO[]>({
    method: 'post',
    url: 'security/login/tenants',
    body: ({ email, password }) => ({
      email: email || "",
      password: password || ""
    })
  }),

  /**
   * Resolves a tenant entry point before anyone has signed in, so the sign-in
   * page can say which workspace it belongs to. 404 means the slug is not a
   * live entry point.
   */
  getTenantEntryPoint: ApiEndpoint<{ slug: string }, { name: string, slug: string }>({
    url: ({ slug }) => `tenants/${encodeURIComponent(slug)}`
  }),

  /**
   * The same answer, for a tenant reached at its OWN hostname instead of `/t/<slug>`.
   *
   * The hostname is passed rather than read from the request, because the request goes to the
   * API — `api.<something>` — and the name the visitor typed is the FRONTEND's. Only verified
   * hostnames of live tenants resolve, so a name somebody registered without ever pointing it
   * here answers 404, exactly like an unknown slug.
   */
  getTenantEntryPointByHost: ApiEndpoint<{ host: string }, { name: string, slug: string }>({
    url: ({ host }) => `tenants/by-host?host=${encodeURIComponent(host)}`
  }),

  verifyMfa: ApiEndpoint<{
    otp: string,
    token?: string
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/verify-mfa',
    body: ({ otp, token }) => ({
        options: { 
            otp: otp || "",
        },
        token: token || ""
    })
  }),

  impersonate: ApiEndpoint<{
    userId: number
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/impersonate',
    body: ({ userId }) => ({
      userId: userId,
    })
  }),

  loginWithToken: ApiEndpoint<{
    token: string
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/login-with-token',
    body: ({ token }) => ({
      token,
    })
  }),

  verifyToken: ApiEndpoint<void, boolean>({
    method: 'post',
    url: 'security/check'
  }),

  /**
   * Starts password recovery.
   *
   * `tenantSlug` narrows which account the link is for. It matters because the
   * reset link lets whoever opens it SET a password: with an address that exists
   * in two tenants and nothing to disambiguate, the server sends no mail at all
   * rather than hand one customer's account to another customer's user. On
   * `/t/<slug>` there IS something to disambiguate, so pass it.
   */
  recoverPassword: ApiEndpoint<{ email: string, tenantSlug?: string | null }, string>({
    method: 'post',
    url: ({ email, tenantSlug }) => (
      `security/password-recovery/${email}`
      + (tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '')
    )
  }),

  resetPassword: ApiEndpoint<{
    token: string,
    password: string,
    passwordConfirm: string
  }, string>({
    method: 'post',
    url: ({ token }) => `security/password-reset/${token}`,
    body: ({ password, passwordConfirm }) => ({
      password,
      passwordConfirm
    })
  }),

  getSelf: ApiEndpoint<void, UserDetailsDTO>({
    url: 'security/self'
  }),

  getSignature: ApiEndpoint<void, any>({
    url: 'security/self/signature'
  }),

  updateSelf: ApiEndpoint<UpdateSelfUserDTO, UserDetailsDTO>({
    method: 'post',
    url: 'security/self',
    body: data => data
  }),

};


/** A tenant as its sign-in door is advertised: enough to name it and to reach it. */
export type TenantEntryPointDTO = {
  name: string
  slug: string
}

export enum MfaChallengeType {
  EMAIL_OTP = 0
};

export type UpdateSelfUserDTO = {
  firstName: string
  lastName: string
  phone: string
  extension: string
  password: string
  passwordConfirm: string
  signature: string
  notifyDesktop: boolean
  notifyEmail: boolean
  timeZoneId: string
  isOnLeave: boolean
  notificationChannels?: string[]
}

export type AuthenticateResponse = {
  user: UserAuthDTO
  token: string
  logOutUrl: string
  mfaVerificationRequired?: boolean
  mfaVerificationType?: MfaChallengeType
}

export type UserDetailsDTO = {
    id: number
    email: string
    createdAt?: JSONDateTime
    isActive: boolean
    isOnLeave: boolean
    hasCompletedProfile: boolean
    changePassword: boolean
    firstName: string
    lastName: string
    organizationalUnitId: number
    phone: string
    extension: string
    position: string
    jediId: number
    accessProfileId: number
    signature: string
    organizationalUnitRoles: UserOrganizationalUnitRoleDTO[]
    branchId: number
    extras: JSON
    notifyDesktop: boolean
    notifyEmail: boolean
    timeZoneId: string
    notificationChannels?: string[]
};

export type UserOrganizationalUnitRoleDTO = {
  id: number
  userId: number
  roleId: number
  organizationalUnitId: number
  mainRole: boolean
  canWorkAs: boolean
  canBeAssigned: boolean
  canAssign: boolean
  canWorkUnassigned: boolean
}

export type UserAuthDTO = {
  id: number,
  firstName: string,
  lastName: string,
  email: string,
  jediId?: number,
  createdAt?: JSONDateTime,
  organizationalUnit: OrganizationalUnitInfoDTO,
  accessProfile: AccessProfileDTO,
  organizationalUnitRoles: UserOrganizationalUnitRoleAuthDTO[],
  isActive: boolean,
  isDual: boolean,
  isSuperuser: boolean,
  hasCompletedProfile: boolean,
  changePassword: boolean,
  position: string,
};
export type OrganizationalUnitInfoDTO = {
  id: number
  tenantId: number
  parentId?: number
  removed: boolean
  code: string
  name: string
  address1: string
  address2: string
  city: string
  postalCode: string
  state: string
  path: string
  numericCode: number
  dependencyFigure: number
  type: string
  workflow: WorkflowDTO
  workflowParams: JSONObject
};
export type WorkflowDTO = {
    id: number
    objectSchemaId: number
    name: string
    description: string
    slug: string
    redirect: string
    viewForm: string
    definition: JSONObject
    paramSchema: JSONObject
    roles: JSONArray
    version: number
    lastUpdated: JSONDateTime
    workflowLayout: JSONObject
};

export type AccessProfileDTO = {
  name: string
  displayName: string
  permissions: string[]
}
export type UserOrganizationalUnitRoleAuthDTO = {
  id: number
  userId: number
  roleId: number
  organizationalUnitId: number
  role: RoleDTO
  organizationalUnit: OrganizationalUnitInfoDTO
  mainRole: boolean
  canWorkAs: boolean
  canBeAssigned: boolean
  canAssign: boolean
  canWorkUnassigned: boolean
};
export type RoleDTO = {
  name: string
  displayName: string
  extras: JSONObject
  entities: JSONObject
  removed: boolean
}



export default AuthApi;
